/**
 * Environment represents the deployment environment of the application.
 */
export type Environment = 'production' | 'staging' | 'dev';

/**
 * Service represents the different frontend applications.
 */
export type Service = 'ais' | 'clients';

/**
 * Backend represents the different backend services.
 */
export type Backend = 'api' | 'statics';

/**
 * Gets the current environment the application is running in.
 * Defaults to 'dev' if running server-side or if environment is not set.
 */
export const getEnvironment = (): Environment => {
  if (typeof window === 'undefined') {
    return 'dev';
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const environment = process.env.OYSTER_ENVIRONMENT || window?.oyster?.opts?.environment;
  switch (environment) {
    case 'production':
    case 'staging':
    case 'dev':
      return environment;
    default:
      return 'dev';
  }
};

/**
 * Gets the base URL for the API service based on the current environment.
 */
export const getApiBaseUrl = (): string => {
  switch (getEnvironment()) {
    case 'production':
      return 'https://api.withoyster.com';
    case 'staging':
      return 'https://api.staging.withoyster.com';
    case 'dev':
      return devServiceUrl('api');
  }
};

/**
 * Gets the base URL for the AIS service based on the current environment.
 */
export const getAisBaseUrl = (): string => {
  switch (getEnvironment()) {
    case 'production':
      return 'https://ais.withoyster.com';
    case 'staging':
      return 'https://ais.staging.withoyster.com';
    case 'dev':
      return devServiceUrl('ais');
  }
};

/**
 * Gets the base URL for the statics service based on the current environment.
 */
export const getStaticsBaseUrl = (): string => {
  switch (getEnvironment()) {
    case 'production':
      return 'https://s.withoyster.com';
    case 'staging':
      return 'https://s.staging.withoyster.com';
    case 'dev':
      return devServiceUrl('statics');
  }
};

/**
 * Constructs a development service URL based on the current user's subdomain.
 * Format: https://{service}.{user}.dev.oysterinc.net
 */
const devServiceUrl = (service: Service | Backend) => {
  // Get the user from the URL
  const [, user] = window.location.host.split('.');

  return `https://${service}.${user}.dev.oysterinc.net`;
};

/**
 * Gets the current service name from environment variables.
 */
export const getServiceName = (): Service => (process.env.SERVICE_NAME as Service) || '';

/**
 * Gets the current service version from environment variables.
 */
export const getServiceVersion = (): string => process.env.SERVICE_VERSION || '';

/**
 * Gets a secret value from either development environment variables or window.oyster.opts.
 */
const getSecret = (secretName: string, devSecret?: string) =>
  devSecret || window?.['oyster']?.['opts']?.[secretName];

/**
 * Gets the Mapbox public API key.
 */
export const getMapboxPublicKey = () => getSecret('mapboxPublicKey', process.env.MAPBOX_PUBLIC_KEY);

/**
 * Gets the Sentry DSN for error reporting.
 */
export const getSentryDSN = () => process.env.SENTRY_DSN;
